import {getTestProps} from '@benchling/js-test-utils';
import {Button, createStyles, Group, Radio, Stack, Table, Text, TextInput} from '@mantine/core';
import {useDebouncedValue} from '@mantine/hooks';
import React, {useEffect, useState} from 'react';

import {InstrumentQueryStatus, SnowflakeTable} from 'instruments/instrument-data/InstrumentDataConstants';
import {
  createListTablesInstrumentQuery,
  pollInstrumentQueryStatus,
} from 'instruments/instrument-data/InstrumentDataDAO';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

// A customer database can hold thousands of tables, so they are listed a page at a time.
export const TABLES_PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 300;

const useStyles = createStyles(() => ({
  selectableRow: {
    cursor: 'pointer',
  },
}));

interface DatalakeTableSelectorProps {
  connectionId: string;
  // The data source the connection lists tables from, e.g. 'Snowflake' or 'Databricks'.
  sourceName: string;
  value: SnowflakeTable | null;
  onChange: (table: SnowflakeTable | null) => void;
}

interface TablesPageRequest {
  search: string;
  offset: number;
}

// Lists the tables available in the connected Snowflake or Databricks database (via the LIST_TABLES
// query), a page at a time and optionally filtered by name, and lets the user pick one
// to export.
export default function DatalakeTableSelector({
  connectionId,
  sourceName,
  value,
  onChange,
}: DatalakeTableSelectorProps): JSX.Element {
  const {classes} = useStyles();
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchInput.trim(), SEARCH_DEBOUNCE_MS);
  const [request, setRequest] = useState<TablesPageRequest>({search: '', offset: 0});
  const [tables, setTables] = useState<Array<SnowflakeTable> | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // A new search starts again from the first page.
  useEffect(() => {
    setRequest((prev) => (prev.search === debouncedSearch ? prev : {search: debouncedSearch, offset: 0}));
  }, [debouncedSearch]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setTables(null);

    createListTablesInstrumentQuery(connectionId, {
      limit: TABLES_PAGE_SIZE,
      offset: request.offset,
      ...(request.search ? {search: request.search} : {}),
    })
      .then(({instrumentQueryId}) => pollInstrumentQueryStatus(instrumentQueryId))
      .then((query) => {
        if (cancelled) {
          return;
        }
        if (query.status === InstrumentQueryStatus.ERROR) {
          setError(query.info || 'Failed to list tables');
        } else {
          // The LIST_TABLES result is returned in the query's `values` payload as
          // `{tables: [...], has_more}` (`info` is just the status string, e.g. "Success").
          const values = (query.values ?? {}) as {tables?: Array<SnowflakeTable>; has_more?: boolean};
          setTables(values.tables ?? []);
          setHasMore(values.has_more ?? false);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [connectionId, request]);

  const selectedKey = value ? `${value.schema_name}.${value.table_name}` : null;

  let content: JSX.Element;
  if (isLoading) {
    content = (
      <>
        <Text size="sm" color="dimmed">
          Loading tables from {sourceName}...
        </Text>
        <CenteredHorizontalThrobber />
      </>
    );
  } else if (error) {
    content = (
      <Text size="sm" color="red">
        Error loading tables: {error}
      </Text>
    );
  } else if (!tables || tables.length === 0) {
    content = (
      <Text size="sm" color="dimmed">
        {request.search
          ? `No tables match "${request.search}".`
          : 'No tables found in the connected database.'}
      </Text>
    );
  } else {
    content = (
      <Table highlightOnHover={true}>
        <thead>
          <tr>
            <th />
            <th>Schema</th>
            <th>Table</th>
          </tr>
        </thead>
        <tbody>
          {tables.map((table) => {
            const key = `${table.schema_name}.${table.table_name}`;
            const isSelected = selectedKey === key;
            const toggle = (): void => {
              onChange(isSelected ? null : table);
            };
            return (
              <tr key={key} className={classes.selectableRow} onClick={toggle}>
                <td>
                  {/* Selection is driven by clicking the row; the radio is a controlled,
                      accessible reflection of that state (readOnly so it doesn't handle
                      its own change and double-toggle). */}
                  <Radio
                    checked={isSelected}
                    readOnly={true}
                    aria-label={`Select ${table.schema_name}.${table.table_name}`}
                  />
                </td>
                <td>{table.schema_name}</td>
                <td>{table.table_name}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  }

  // The pager stays mounted, and its buttons stay enabled, while a page loads so the
  // button the user just clicked keeps focus; clicks are ignored until the page arrives.
  const showPager = !error && (request.offset > 0 || hasMore);

  return (
    <Stack spacing="xs" {...getTestProps({component: 'DatalakeTableSelector', element: 'root'})}>
      <TextInput
        label="Search tables"
        placeholder="Filter by table name"
        value={searchInput}
        onChange={(e) => {
          setSearchInput(e.currentTarget.value);
        }}
      />
      {content}
      {showPager && (
        <Group position="apart">
          <Text size="sm" color="dimmed">
            {!isLoading && tables && tables.length > 0
              ? `Showing tables ${request.offset + 1}–${request.offset + tables.length}`
              : null}
          </Text>
          <Group spacing="xs">
            <Button
              variant="secondary"
              size="xs"
              disabled={request.offset === 0}
              aria-disabled={isLoading}
              onClick={() => {
                if (isLoading) {
                  return;
                }
                setRequest((prev) => ({...prev, offset: Math.max(0, prev.offset - TABLES_PAGE_SIZE)}));
              }}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="xs"
              disabled={!hasMore}
              aria-disabled={isLoading}
              onClick={() => {
                if (isLoading) {
                  return;
                }
                setRequest((prev) => ({...prev, offset: prev.offset + TABLES_PAGE_SIZE}));
              }}
            >
              Next
            </Button>
          </Group>
        </Group>
      )}
    </Stack>
  );
}
